import { useCallback, useSyncExternalStore } from 'react'

/**
 * `matchMedia`, as a React store.
 *
 * This was `react-responsive`'s `useMediaQuery`, which is the whole of what
 * the package did for this app -- one hook, wrapping the same browser API.
 * `useSyncExternalStore` subscribes to the list's own `change` event, so a
 * window crossing a breakpoint re-renders exactly the components that asked.
 */
const useMediaQuery = (query: string) => {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia(query)
      list.addEventListener('change', onChange)
      return () => {
        list.removeEventListener('change', onChange)
      }
    },
    [query],
  )

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  )
}

/**
 * The app's breakpoints, in px, mirroring the `--breakpoint-*` values in
 * `src/tailwind.css`. `useBreakpoint.test.ts` reads that file and asserts the
 * two agree, so this cannot drift.
 */
export const breakpoints = {
  xs: 480,
  sm: 640,
  md: 768,
  lg: 1024,
  blg: 1200,
  xl: 1280,
  xlg: 1536,
  xxl: 1920,
} as const

export type Breakpoint = keyof typeof breakpoints

/**
 * True below `name` -- the exact complement of Tailwind's `name:` prefix, and
 * therefore the JS twin of `max-<name>:`.
 *
 * Written as a negated `min-width` on purpose. Tailwind emits `(width >= 64rem)`
 * for `lg:`, so its complement is `width < 1024`, whereas the seventeen
 * hand-written queries this replaces all said `(max-width: 1024px)` -- which is
 * INCLUSIVE and so overlapped `lg:` at exactly 1024px. Every one of them was off
 * by one, and at 1024 (iPad landscape, or a 1280 window at 125% zoom) that was
 * visible: `DashboardLayout` reserved its `lg:pl-…` rail gutter for a sidenav
 * that `useSidenav` had already slid off-screen. Negating min-width also avoids
 * inventing a `1023.98px` fudge, so there is no rounding surface at all.
 */
export const useBelow = (name: Breakpoint) =>
  !useMediaQuery(`(min-width: ${breakpoints[name]}px)`)

/** True at or above `name`, matching Tailwind's `name:` prefix exactly. */
export const useAtLeast = (name: Breakpoint) =>
  useMediaQuery(`(min-width: ${breakpoints[name]}px)`)
